/**
 * CategoryEditModal — create/edit kategorii.
 * 2-step flow: ikona → kolor (jak legacy js/settings.js).
 */
import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import SegmentedControl from '../ui/SegmentedControl';
import { type Category } from '../../features/categories/categoriesStore';
import { createCategory, updateCategory, deleteCategory } from '../../features/categories/api';
import { useAuthStore } from '../../features/auth/authStore';
import { getCategoryIcon } from '../../lib/categoryIcons';
import { safeColor, haptic } from '../../lib/utils';
import { showToast } from '../ui/Toast';

const ICON_KEYS = [
  'shopping_cart', 'restaurant', 'shopping_bag', 'car', 'health', 'home', 'bills', 'child',
  'movie', 'sport', 'sparkles', 'travel', 'education', 'gift', 'pets', 'trending_up',
  'banknote', 'family', 'bank', 'coins', 'piggy', 'more',
];

const COLORS = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1', '#3949AB', '#1E88E5', '#039BE5', '#00ACC1',
  '#00897B', '#43A047', '#7CB342', '#F4511E', '#FB8C00', '#FFB300', '#F6BF26', '#9E9E9E',
  '#EC407A', '#AB47BC', '#5C6BC0', '#26A69A',
];

interface Props {
  category: Category | null;
  defaultType?: 'income' | 'expense';
  onClose: () => void;
}

export function CategoryEditModal({ category, defaultType = 'expense', onClose }: Props) {
  const family = useAuthStore((s) => s.family);

  const isEdit = !!category;
  const [name, setName] = useState(category?.name ?? '');
  const [type, setType] = useState<'income' | 'expense'>(category?.type ?? defaultType);
  const [icon, setIcon] = useState(category?.icon ?? ICON_KEYS[0]!);
  const [color, setColor] = useState(category?.color ?? COLORS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const Icon = getCategoryIcon(icon);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Podaj nazwę kategorii');
      return;
    }
    if (!family) return;
    setSubmitting(true);
    try {
      if (isEdit && category) {
        await updateCategory(category.id, { name: name.trim(), icon, color, type });
        showToast('Zaktualizowano kategorię', 'success');
      } else {
        await createCategory({
          familyId: family.id,
          name: name.trim(),
          icon,
          color,
          type,
        });
        showToast('Kategoria utworzona', 'success');
      }
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zapisać');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!category) return;
    if (category.isDefault) {
      showToast('Nie można usunąć kategorii domyślnej', 'warn');
      return;
    }
    if (!window.confirm(`Usunąć kategorię "${category.name}"? Transakcje zachowują przypisanie ID.`)) return;
    setSubmitting(true);
    try {
      await deleteCategory(category.id);
      showToast('Kategoria usunięta', 'info');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się usunąć');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size="md"
      icon={
        <div className="w-16 h-16 rounded-full ring-8 flex items-center justify-center" style={{ backgroundColor: `${safeColor(color)}22`, color: safeColor(color), boxShadow: `0 0 0 8px ${safeColor(color)}11` }}>
          <Icon size={28} strokeWidth={2.25} />
        </div>
      }
      title={isEdit ? 'Edycja kategorii' : 'Nowa kategoria'}
      footer={
        <div className="flex gap-2">
          {isEdit && !category?.isDefault && (
            <button type="button" onClick={handleDelete} disabled={submitting} className="px-3 py-3 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 transition disabled:opacity-50">
              <Icons.Trash size={16} />
            </button>
          )}
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50">Anuluj</button>
          <button type="button" onClick={handleSave} disabled={submitting} className="flex-[1.5] px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition disabled:opacity-50">{submitting ? 'Zapisuję...' : isEdit ? 'Zapisz' : 'Utwórz'}</button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Nazwa</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. Spożywcze"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 text-sm"
          />
        </div>

        <SegmentedControl<'expense' | 'income'>
          options={[
            { key: 'expense', label: 'Wydatek' },
            { key: 'income', label: 'Przychód' },
          ]}
          value={type}
          onChange={setType}
          fullWidth
        />

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Ikona</label>
          <div className="grid grid-cols-6 gap-2">
            {ICON_KEYS.map((key) => {
              const Ic = getCategoryIcon(key);
              const sel = key === icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  aria-pressed={sel}
                  className={`h-12 rounded-xl flex items-center justify-center border transition ${sel ? 'border-sky-500 ring-2 ring-sky-500/20 bg-sky-50 dark:bg-sky-500/10' : 'border-slate-200 dark:border-slate-700'}`}
                >
                  <Ic size={18} className="text-slate-600 dark:text-slate-300" />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kolor</label>
          <div className="grid grid-cols-10 gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Kolor ${c}`}
                aria-pressed={c === color}
                className={`w-8 h-8 rounded-full transition ${c === color ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-sky-500' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">{error}</div>
        )}
      </div>
    </Modal>
  );
}
